import { create } from 'zustand';

// ─────────────────────────────────────────────────────────────
// Activity Store — session/local "recent edits" log.
// Each successful CRUD action is pushed onto a capped log persisted
// in localStorage. The TopBar "recent edits" control reads from here.
// ─────────────────────────────────────────────────────────────

export type ActivityType = 'create' | 'update' | 'delete';

export interface Activity {
  id: string;
  type: ActivityType;
  entity: string;
  label: string;
  timestamp: number;
  user?: string;
}

interface ActivityStore {
  activities: Activity[];
  unreadCount: number;
  record: (a: Omit<Activity, 'id' | 'timestamp'>) => void;
  markAllRead: () => void;
  clear: () => void;
}

const STORAGE_KEY = 'geo_recent_activities';
const UNREAD_KEY = 'geo_recent_activities_unread';
const MAX_ACTIVITIES = 12;

function loadFromStorage(): { activities: Activity[]; unreadCount: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const activities: Activity[] = raw ? JSON.parse(raw) : [];
    const unreadCount = Number(localStorage.getItem(UNREAD_KEY) ?? 0) || 0;
    return { activities, unreadCount };
  } catch {
    return { activities: [], unreadCount: 0 };
  }
}

function persist(activities: Activity[], unreadCount: number) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(activities));
    localStorage.setItem(UNREAD_KEY, String(unreadCount));
  } catch {
    /* localStorage full or disabled — best-effort */
  }
}

const initial = loadFromStorage();

export const useActivityStore = create<ActivityStore>((set, get) => ({
  activities: initial.activities,
  unreadCount: initial.unreadCount,
  record: (a) => {
    const entry: Activity = {
      ...a,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
    };
    const next = [entry, ...get().activities].slice(0, MAX_ACTIVITIES);
    const unread = Math.min(MAX_ACTIVITIES, get().unreadCount + 1);
    persist(next, unread);
    set({ activities: next, unreadCount: unread });
  },
  markAllRead: () => {
    persist(get().activities, 0);
    set({ unreadCount: 0 });
  },
  clear: () => {
    persist([], 0);
    set({ activities: [], unreadCount: 0 });
  },
}));

// ── Time-ago helper (used by the recent-edits dropdown) ──────
export function timeAgo(ts: number): string {
  const sec = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}
